import { http } from '@/lib/ats-https';
import type {
  AgentForgeBulkActivitySnapshot,
  BulkOperationProgress,
  BulkOperationStatus,
  BulkOperationTarget,
} from '../../../types/blocks/bulkOperation';

const RECENT_ACTIVITY_LIMIT = 100;

export const agentForgeBulkActivityKeys = {
  all: ['agentforge', 'bulk-activities'] as const,
  recent: (scope: string) =>
    [...agentForgeBulkActivityKeys.all, 'recent', scope] as const,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const numberOrZero = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const optionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const isStatus = (value: unknown): value is BulkOperationStatus =>
  value === 'pending' ||
  value === 'in_progress' ||
  value === 'completed' ||
  value === 'failed';

const normalizeProgress = (value: unknown): BulkOperationProgress => {
  const progress = isRecord(value) ? value : {};
  return {
    total: numberOrZero(progress.total),
    processed: numberOrZero(progress.processed),
    success: numberOrZero(progress.success),
    failed: numberOrZero(progress.failed),
    skipped: numberOrZero(progress.skipped),
    percentage: numberOrZero(progress.percentage),
  };
};

const normalizeTarget = (value: unknown): BulkOperationTarget | undefined => {
  if (!isRecord(value)) return undefined;
  const id = numberOrZero(value.id);
  const type = optionalString(value.type);
  if (!id || !type) return undefined;
  return {
    id,
    type,
    ...(optionalString(value.name) ? { name: String(value.name) } : {}),
    ...(optionalString(value.url) ? { url: String(value.url) } : {}),
  };
};

const normalizeJob = (
  value: unknown
): AgentForgeBulkActivitySnapshot['ats_job'] => {
  if (!isRecord(value)) return undefined;
  const id = numberOrZero(value.id);
  const name = optionalString(value.name);
  if (!id || !name) return undefined;
  return {
    id,
    name,
    ...(optionalString(value.url) ? { url: String(value.url) } : {}),
  };
};

export const normalizeAgentForgeBulkActivity = (
  value: unknown
): AgentForgeBulkActivitySnapshot | null => {
  if (!isRecord(value)) return null;
  const id = numberOrZero(value.id);
  const activity = optionalString(value.activity);
  if (!id || !activity || !isStatus(value.status)) return null;
  const atsJob = normalizeJob(value.ats_job);
  const target = normalizeTarget(value.target);

  return {
    id,
    activity,
    status: value.status,
    progress: normalizeProgress(value.progress),
    ...(optionalString(value.created_at)
      ? { created_at: String(value.created_at) }
      : {}),
    ...(optionalString(value.updated_at)
      ? { updated_at: String(value.updated_at) }
      : {}),
    ...(optionalString(value.job_name)
      ? { job_name: String(value.job_name) }
      : {}),
    ...(atsJob ? { ats_job: atsJob } : {}),
    ...(target ? { target } : {}),
  };
};

const activityArray = (response: unknown): unknown[] => {
  if (Array.isArray(response)) return response;
  if (!isRecord(response)) return [];
  if (Array.isArray(response.data)) return response.data;
  if (isRecord(response.data) && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return [];
};

/** Fetches an unfiltered recent window. `skip_viewed` is intentionally absent:
 * a chat card must retain access to its exact operation after it is viewed. */
export const listAgentForgeBulkActivities = async (): Promise<
  AgentForgeBulkActivitySnapshot[]
> => {
  const response = (await http.get('/bulk_activities', {
    params: { limit: RECENT_ACTIVITY_LIMIT },
  })) as unknown;

  return activityArray(response)
    .map(normalizeAgentForgeBulkActivity)
    .filter(
      (activity): activity is AgentForgeBulkActivitySnapshot =>
        activity !== null && activity.activity !== 'automation_executed'
    );
};
